import { useId, useMemo, useState } from 'react'
import { formatErrorDetail } from '@/lib/bootstrapError'
import { CopyOverlay } from '@/shared/components/CopyOverlay'
import s from '@/app/AppErrorScreen.module.css'

interface AppErrorScreenProps {
  /** Heading. Also the (deliberately unadvertised) toggle for the diagnostic. */
  title?: string
  /** Friendly one-liner under the heading. */
  message?: string
  /** Short failure-point label, e.g. 'signin-redirect'. Shown in small print so a
   *  support screenshot carries triage signal even without the detail open. */
  stage: string
  /** The captured failure. A string is shown verbatim (pre-formatted detail from
   *  sessionStorage); anything else goes through formatErrorDetail. */
  error?: unknown
  action?: { label: string; onClick: () => void }
}

/** Full-page bootstrap error surface. The diagnostic detail is never shown by
 *  default: clicking the title toggles it, so an operator can triage quietly in
 *  front of users. The title is a real button (keyboard/screen-reader operable)
 *  styled as plain heading text. */
export function AppErrorScreen({
  title = 'Something went wrong',
  message = 'The app could not start. Please retry.',
  stage,
  error,
  action,
}: AppErrorScreenProps) {
  const [showDetail, setShowDetail] = useState(false)
  const detailId = useId()
  // Memoised so the embedded timestamp reflects when the failure surfaced, not
  // whenever the detail was last toggled open.
  const detail = useMemo(
    () => (typeof error === 'string' ? error : formatErrorDetail(error, stage)),
    [error, stage],
  )

  return (
    <div className={s.screen}>
      <h1 className={s.title}>
        <button
          type="button"
          className={s.titleButton}
          onClick={() => setShowDetail((v) => !v)}
          aria-expanded={showDetail}
          aria-controls={showDetail ? detailId : undefined}
        >
          {title}
        </button>
      </h1>
      <p className={s.message}>{message}</p>
      <p className={s.stage}>({stage})</p>
      {action && (
        <button type="button" className={s.action} onClick={action.onClick}>
          {action.label}
        </button>
      )}
      {showDetail && (
        <div className={s.detailWrap}>
          <CopyOverlay value={detail} label="Copy diagnostic" />
          <pre id={detailId} className={s.detail}>{detail}</pre>
        </div>
      )}
    </div>
  )
}
